import {useNavigate, useParams} from "react-router-dom";
import React, {useEffect, useState} from "react";
import {getById} from "../_request";
import {IAxiosGetResponse, IServiceDto, ITransactionRouteDto} from "../../interfaces/IUtilities";
import {useTranslation} from "react-i18next";
import {useToast} from "../../modules/layout/components/ToastContext";
import AppBreadcrumb from "../../modules/layout/components/AppBreadcrumb";
import {
    AuthenticationTypeText,
    OperationTypeText,
    RequestPayloadTypeText,
    ServiceConsumingTypeText,
    TransactionMethodText
} from "../../helpers/EnumeratorHelper";
import Loader from "../../modules/layout/components/Loader";


const TransactionDetails: React.FC = () => {

    const {t} = useTranslation();
    const {showToast} = useToast();
    const [service, setService] = useState({} as IServiceDto);
    const {serviceid, id} = useParams();
    const [transactionRoute, setTransactionRoute] = useState({} as ITransactionRouteDto);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);

    const fetchServiceDetails = async () => {
        try {
            let result = await getById("/api/services/", serviceid!);
            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setService(result.data as IServiceDto);
            }
            setLoading(false);

        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', {error: String(error)}));
            showToast(t('ConnectionError', {error: String(error)}), 'error');
        }
    }

    const fetchData = async () => {
        setLoading(true);
        try {
            let result = await getById(`/api/transactionroutes/`, id!);

            let respStatus = result?.status || result?.response.status;

            if (respStatus === 200) {
                setTransactionRoute(result.data as ITransactionRouteDto);
                setLoading(false);
            } else if (respStatus === 403) {
                navigate("/error/403");
            } else if (respStatus === 401) {
                navigate("/auth/login");
            } else if (respStatus === 500) {
                navigate("/error/500");
            } else {
                throw new Error(t('StatusCode', {code: String(respStatus)}));
            }
        } catch (error) {
            setLoading(false);
            console.log(t('ConnectionError', {error: String(error)}));
            showToast(t('ConnectionError', {error: String(error)}), 'error');
            navigate("/services/list");
        }
    }

    useEffect(() => {
        fetchServiceDetails();
        fetchData();
    }, [serviceid]);

    const uniqueProfileTypes = React.useMemo(() =>
        (transactionRoute?.profileTypeList ?? []).filter(
        (value, index, self) => self.indexOf(value) === index
        ),
    [transactionRoute?.profileTypeList]);


    if (loading) return <Loader/>

    return (
        <div className="container mt-4 gg-container-card">
            <AppBreadcrumb
                items={[
                    {title: t('Home'), href: "/"},
                    {title: t('Services'), href: "/services/list"},
                    {title: t('BreadcrumbServiceTransationRoutes', { serviceName: service.name}), href: `/services/${serviceid}/transactions/list`}, // Active page (no href)
                    {title: t('Details')} // Active page (no href)
                ]}/>

            <h3 className="mb-4">{t('TransationRouteDetails')}</h3>

            {loading && (<Loader />)}

            <div className="card">
                <div className="card-header">
                    <div className="row">
                        <div className="col"><h4 className="mb-0">{transactionRoute.routeLabel}</h4></div>
                        <div className="col-auto align-self-center">
                            {transactionRoute.enabled ?
                                <span className="badge bg-success">{t('Enabled')}</span> :
                                <span className="badge bg-secondary">{t('Disabled')}</span>
                            }
                        </div>
                    </div>
                </div>
                <div className="card-body">
                    <p><strong>{t('ClassName')}:</strong> {transactionRoute.className}</p>
                    <p><strong>{t('Location')}:</strong> {transactionRoute.postLocation}</p>
                    <p><strong>{t('AccessTokenUrl')}:</strong> {transactionRoute.accessTokenUrl}</p>
                    <p><strong>{t('ServiceConsumingType')}:</strong> {ServiceConsumingTypeText[transactionRoute.serviceConsumingType]}</p>
                    <p><strong>{t('TransactionMethod')}:</strong> {TransactionMethodText[transactionRoute && transactionRoute.transactionMethod!]}</p>
                    <p><strong>{t('AuthenticationType')}:</strong> {AuthenticationTypeText[transactionRoute && transactionRoute.authenticationType!]}</p>
                    <p><strong>{t('RequestPayloadType')}:</strong> {RequestPayloadTypeText[transactionRoute && transactionRoute.requestPayloadType!]}</p>
                    <p><strong>{t('RetryInterval')}:</strong> {transactionRoute.retryInterval}</p>
                    <p><strong>{t('OperationType')}:</strong> {OperationTypeText[transactionRoute.operationType]}</p>
                    <p><strong>{t('SpokeSubmissionOnly')}:</strong> {transactionRoute.spokeSubmissionOnly ? t('Yes') : t('No')}</p>
                    <div><strong>{t('ProfileType')}:</strong>
                        {uniqueProfileTypes.length > 0 ? (
                            <ul>
                                {uniqueProfileTypes.map((field, idx) => (
                                    <li key={idx}>{field}</li>
                                ))}
                            </ul>
                        ) : (
                            <p>" - "</p>
                        )}
                    </div>
                </div>
                <div className="card-footer bg-transparent">
                    <button type="button" className="btn btn-secondary me-2" onClick={() => navigate('/services/list')}>
                        {t('BackToServicesList')}
                    </button>
                    <button type="button" className="btn btn-warning me-2"
                            onClick={() => navigate(`/services/${serviceid}/transactions/list`)}>
                        {t('BackToTransactionRoutesList')}
                    </button>
                </div>
            </div>

        </div>)
}

export default TransactionDetails;